import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { SearchTrigger } from "@/components/dex/SearchTrigger";
import beamLogo from "@/assets/beam-logo.png";
import { SolutionsMenu, SolutionsMenuMobile } from "@/components/SolutionsMenu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface SiteHeaderProps {
  cta?: ReactNode;
}

/**
 * Universal top navigation: logo + Solutions dropdowns (desktop) + hamburger (mobile) + optional CTA slot.
 * Replaces per-page bespoke navs across audience landing pages.
 */
export function SiteHeader({ cta }: SiteHeaderProps) {
  const [open, setOpen] = useState(false);

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border/20"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-6 py-2.5">
        <Link to="/" className="shrink-0">
          <motion.img
            src={beamLogo}
            alt="Beam Healthcare"
            className="h-[2.88rem] md:h-[3.84rem] brightness-0"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400 }}
          />
        </Link>

        <SolutionsMenu className="ml-auto mr-2" />

        <div className="flex items-center gap-2">
          <SearchTrigger className="md:hidden" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              aria-label="Open menu"
              className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-foreground/70 hover:bg-foreground/5 hover:text-foreground"
            >
              <Menu className="h-6 w-6" />
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] sm:w-[380px] overflow-y-auto">
              <div className="mt-8">
                <SolutionsMenuMobile onNavigate={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
          {cta}
        </div>
      </div>
    </motion.nav>
  );
}
